import { useEffect, useMemo, useRef, useState } from 'react';
import { loadExternalScript } from '../utils/scriptLoader';
import { DISCORD_CSV_BY_YEAR, YTD_CSV_BY_YEAR, getCsvUrls } from '../utils/dataSources';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const YTD_CSVS = getCsvUrls(YTD_CSV_BY_YEAR);
const DISCORD_CSVS = getCsvUrls(DISCORD_CSV_BY_YEAR);

export const OVERVIEW_YEAR = new Date().getFullYear();

const formatCurrency = (value) => {
  if (value == null) return '—';
  const absolute = Math.abs(value);
  const formatted = absolute.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

const applyYearlyChanges = (series) => {
  const byYear = new Map();
  series.forEach((entry) => {
    const year = entry.date.getFullYear();
    if (!byYear.has(year)) {
      byYear.set(year, []);
    }
    byYear.get(year).push(entry);
  });

  const combined = [];
  for (const entries of byYear.values()) {
    entries.sort((a, b) => a.date - b.date);
    for (let i = 1; i < entries.length; i += 1) {
      entries[i].change = entries[i].cumulative - entries[i - 1].cumulative;
    }
    if (entries.length) {
      entries[0].change = 0;
    }
    combined.push(...entries);
  }

  return combined.sort((a, b) => a.date - b.date);
};

const buildDailyOverview = (mainSeries, theoreticalSeries) => {
  const defaults = {
    currentPosition: null,
    currentPositionFormatted: '—',
    currentPositionTone: 'neutral',
    highWaterMarkFormatted: '—',
    theoreticalHWMFormatted: '—',
    theoreticalHWMTone: 'neutral',
    statusText: '—',
    statusTone: 'neutral',
    biggestWinningDayText: '—',
    biggestLosingDayText: '—',
    longestWinningStreakText: '—',
    longestLosingStreakText: '—',
    currentWinningStreak: 0,
    currentWinningStreakDescription: 'No active winning streak',
    streakIntensity: 0,
    donutSegments: [],
    heatmapEntries: [],
  };

  if (!mainSeries || mainSeries.length === 0) {
    return defaults;
  }

  const latestPoint = mainSeries[mainSeries.length - 1];
  const currentPosition = latestPoint.cumulative;
  const currentPositionTone = currentPosition > 0 ? 'positive' : currentPosition < 0 ? 'negative' : 'neutral';
  const statusText = currentPosition > 0 ? '▲ Up' : currentPosition < 0 ? '▼ Down' : '—';
  const statusTone = currentPosition > 0 ? 'positive' : currentPosition < 0 ? 'negative' : 'neutral';

  const highWaterMark = Math.max(...mainSeries.map((entry) => entry.cumulative));

  const theoreticalHWMValue =
    theoreticalSeries && theoreticalSeries.length ? theoreticalSeries[theoreticalSeries.length - 1].hwm : null;
  const theoreticalHWMTone = theoreticalHWMValue > 0 ? 'positive' : theoreticalHWMValue < 0 ? 'negative' : 'neutral';

  let biggestWinning = null;
  let biggestLosing = null;
  let winningDays = 0;
  let losingDays = 0;
  let breakEvenDays = 0;

  let currentWinningStreak = 0;

  for (let i = 1; i < mainSeries.length; i += 1) {
    const change = mainSeries[i].change ?? mainSeries[i].cumulative - mainSeries[i - 1].cumulative;
    if (change > 0) {
      winningDays += 1;
      if (!biggestWinning || change > biggestWinning.change) {
        biggestWinning = { date: mainSeries[i].date, change };
      }
    } else if (change < 0) {
      losingDays += 1;
      if (!biggestLosing || change < biggestLosing.change) {
        biggestLosing = { date: mainSeries[i].date, change };
      }
    } else {
      breakEvenDays += 1;
    }
  }

  const biggestWinningDayText = biggestWinning
    ? `${formatDate(biggestWinning.date)} (${formatCurrency(biggestWinning.change)})`
    : '—';
  const biggestLosingDayText = biggestLosing
    ? `${formatDate(biggestLosing.date)} (${formatCurrency(biggestLosing.change)})`
    : '—';

  const donutSegments = [
    { label: 'Winning Days', count: winningDays, color: '#96e6ab' },
    { label: 'Losing Days', count: losingDays, color: '#df87a8' },
    { label: 'Break Even Days', count: breakEvenDays, color: '#76ace0' },
  ];

  const heatmapEntries = mainSeries
    .filter((entry) => entry.date.getFullYear() === OVERVIEW_YEAR)
    .map((entry) => ({ date: entry.date, change: entry.change ?? 0 }));

  for (let i = mainSeries.length - 1; i >= 0; i -= 1) {
    const change = mainSeries[i].change ?? (i > 0 ? mainSeries[i].cumulative - mainSeries[i - 1].cumulative : 0);
    if (change > 0) {
      currentWinningStreak += 1;
    } else {
      break;
    }
  }

  const streakIntensity = Math.min(100, Math.max(0, currentWinningStreak * 25));
  const currentWinningStreakDescription =
    currentWinningStreak > 0
      ? `${currentWinningStreak} day${currentWinningStreak === 1 ? '' : 's'} on a winning streak`
      : 'No active winning streak';

  return {
    currentPosition,
    currentPositionFormatted: formatCurrency(currentPosition),
    currentPositionTone,
    highWaterMarkFormatted: formatCurrency(highWaterMark),
    theoreticalHWMFormatted: formatCurrency(theoreticalHWMValue),
    theoreticalHWMTone,
    statusText,
    statusTone,
    biggestWinningDayText,
    biggestLosingDayText,
    longestWinningStreakText: winningDays ? `${winningDays} days` : '—',
    longestLosingStreakText: losingDays ? `${losingDays} days` : '—',
    currentWinningStreak,
    currentWinningStreakDescription,
    streakIntensity,
    donutSegments,
    heatmapEntries,
  };
};

export function useOverviewData(isEnabled = true) {
  const [mainSeries, setMainSeries] = useState([]);
  const [theoreticalSeries, setTheoreticalSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isEnabled || hasFetchedRef.current) {
      return undefined;
    }

    let cancelled = false;
    const abortController = new AbortController();
    hasFetchedRef.current = true;

    async function fetchData() {
      setLoading(true);
      try {
        const d3 = await loadExternalScript(D3_URL, 'd3');
        if (cancelled) return;

        const parseMainDate = d3.timeParse('%d/%m/%Y');
        const parseDiscordDate = d3.timeParse('%m/%d/%y');
        const cleanCurrency = (str) => {
          if (!str || str.trim() === '') return null;
          return +str.replace(/\(/g, '-').replace(/\)/g, '').replace(/[$,]/g, '');
        };

        const fetchOptions = { signal: abortController.signal };
        const [rawMainSets, rawDiscordSets] = await Promise.all([
          Promise.all(YTD_CSVS.map((url) => d3.csv(url, fetchOptions))),
          Promise.all(DISCORD_CSVS.map((url) => d3.csv(url, fetchOptions))),
        ]);
        const rawMain = rawMainSets.flat();
        const rawDiscord = rawDiscordSets.flat();
        if (cancelled) return;

        const processedMain = rawMain
          .map((row) => {
            const date = row['Date'] ? parseMainDate(row['Date'].trim()) : null;
            const cumulative = cleanCurrency(row['Runnign Total']);
            if (!date || cumulative == null) return null;
            return { date, cumulative };
          })
          .filter(Boolean);
        const mainWithChanges = applyYearlyChanges(processedMain);

        const processedDiscord = rawDiscord
          .map((row) => {
            const date = row.date ? parseDiscordDate(row.date.trim()) : null;
            const change = cleanCurrency(row.change ?? row.Change);
            const hwm = cleanCurrency(row.hwm_ytd ?? row['High Water Mark']);
            if (!date || hwm == null) return null;
            const entry = { date, hwm };
            if (change != null) entry.change = change;
            return entry;
          })
          .filter(Boolean)
          .sort((a, b) => a.date - b.date);

        if (!cancelled) {
          setMainSeries(mainWithChanges);
          setTheoreticalSeries(processedDiscord);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err instanceof Error ? err.message : String(err));
          hasFetchedRef.current = false;
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
      hasFetchedRef.current = false;
      abortController.abort();
    };
  }, [isEnabled]);

  const stats = useMemo(() => buildDailyOverview(mainSeries, theoreticalSeries), [mainSeries, theoreticalSeries]);

  return {
    mainSeries,
    theoreticalSeries,
    stats,
    loading,
    error,
  };
}

export default useOverviewData;
